import { baseApi } from '@/shared/api/baseApi.js';
import type { PublicUserProfile, UserProfile } from '@prostostroy/shared';
import type {
  AuthResponse,
  RegisterRequest,
  RegisterPendingResponse,
  LoginRequest,
  VerifyEmailRequest,
  ResendCodeRequest,
} from './auth.types.js';

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    register: builder.mutation<{ success: true; data: RegisterPendingResponse }, RegisterRequest>({
      query: (body) => ({
        url: '/auth/register',
        method: 'POST',
        body,
      }),
    }),

    verifyEmail: builder.mutation<{ success: true; data: AuthResponse }, VerifyEmailRequest>({
      query: (body) => ({
        url: '/auth/verify-email',
        method: 'POST',
        body,
      }),
    }),

    resendVerificationCode: builder.mutation<
      { success: true; data: { message: string } },
      ResendCodeRequest
    >({
      query: (body) => ({
        url: '/auth/resend-code',
        method: 'POST',
        body,
      }),
    }),

    login: builder.mutation<{ success: true; data: AuthResponse }, LoginRequest>({
      query: (body) => ({
        url: '/auth/login',
        method: 'POST',
        body,
      }),
    }),

    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),

    getProfile: builder.query<{ success: true; data: UserProfile }, void>({
      query: () => '/profile',
      providesTags: ['User'],
    }),

    updateProfile: builder.mutation<
      { success: true; data: UserProfile },
      Partial<{ name: string; username: string | null; phone: string | null; city: string | null }>
    >({
      query: (body) => ({
        url: '/profile',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User'],
    }),

    searchUsers: builder.query<
      {
        success: true;
        data: {
          id: string;
          name: string;
          username: string | null;
          email: string;
          avatarUrl: string | null;
          role: string;
        }[];
      },
      string
    >({
      query: (q) => `/profile/search?q=${encodeURIComponent(q)}`,
    }),

    uploadAvatar: builder.mutation<{ success: true; data: { avatarUrl: string } }, FormData>({
      query: (formData) => ({
        url: '/profile/avatar',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      invalidatesTags: ['User', 'Conversations'],
    }),

    deleteAvatar: builder.mutation<{ success: true }, void>({
      query: () => ({
        url: '/profile/avatar',
        method: 'DELETE',
      }),
      invalidatesTags: ['User', 'Conversations'],
    }),

    updateOnlinePrivacy: builder.mutation<
      { success: true; data: { showOnlineStatus: boolean } },
      { showOnlineStatus: boolean }
    >({
      query: (body) => ({ url: '/profile/online-privacy', method: 'PATCH', body }),
      invalidatesTags: ['User'],
    }),

    updateProfilePrivacy: builder.mutation<
      { success: true; data: { isPrivate: boolean } },
      { isPrivate: boolean }
    >({
      query: (body) => ({ url: '/profile/privacy', method: 'PATCH', body }),
      invalidatesTags: ['User'],
    }),

    getPublicUser: builder.query<{ success: true; data: PublicUserProfile }, string>({
      query: (userId) => `/profile/users/${userId}`,
      providesTags: (_r, _e, id) => [{ type: 'User' as const, id }],
    }),
  }),
});

export const {
  useRegisterMutation,
  useVerifyEmailMutation,
  useResendVerificationCodeMutation,
  useLoginMutation,
  useLogoutMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useLazySearchUsersQuery,
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
  useUpdateOnlinePrivacyMutation,
  useUpdateProfilePrivacyMutation,
  useGetPublicUserQuery,
} = authApi;
